import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';

const STEPS = [
  {
    num: '01',
    icon: 'ri-price-tag-3-line',
    titleKey: 'activation_step1_title',
    descKey: 'activation_step1_desc',
    detailKey: 'activation_step1_detail',
    detailIcon: 'ri-money-dollar-circle-line',
    color: '#00cfff',
    scrollTo: '#pricing',
  },
  {
    num: '02',
    icon: 'ri-file-text-line',
    titleKey: 'activation_step2_title',
    descKey: 'activation_step2_desc',
    detailKey: 'activation_step2_detail',
    detailIcon: 'ri-time-line',
    color: '#00ff9d',
    scrollTo: '#contact',
  },
  {
    num: '03',
    icon: 'ri-rocket-2-line',
    titleKey: 'activation_step3_title',
    descKey: 'activation_step3_desc',
    detailKey: 'activation_step3_detail',
    detailIcon: 'ri-check-double-line',
    color: '#facc15',
    scrollTo: null,
  },
];

export default function ActivationProcess() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="activation"
      ref={sectionRef}
      className={`py-14 px-4 sm:px-6 md:py-24 relative overflow-hidden ${isLight ? 'bg-[#f0f4fc]' : 'bg-navy-950'}`}
    >
      {/* Background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[400px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(0,207,255,0.05) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[400px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(0,255,157,0.04) 0%, transparent 70%)' }} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">

        {/* Header */}
        <div
          className="text-center mb-10 md:mb-16"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(28px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          <span className="inline-block text-electric text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2 md:mb-3">
            {t('activation_label')}
          </span>
          <h2 className={`font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 ${isLight ? 'text-navy-950' : 'text-white'}`}>
            {t('activation_title_1')}{' '}
            <span className="text-electric">{t('activation_title_2')}</span>
          </h2>
          <p className={`font-body text-sm md:text-base max-w-md mx-auto ${isLight ? 'text-navy-900/55' : 'text-white/55'}`}>
            {t('activation_sub')}
          </p>
        </div>

        {/* Steps row */}
        <div className="flex flex-col lg:flex-row items-stretch gap-4 lg:gap-0">
          {STEPS.map((step, i) => (
            <Fragment key={step.num}>
              <div className="flex-1">
                <StepCard step={step} index={i} visible={visible} isLight={isLight} t={t} />
              </div>

              {/* Arrow divider — desktop only */}
              {i < STEPS.length - 1 && (
                <div
                  className="hidden lg:flex flex-col items-center justify-center flex-shrink-0 w-14 gap-1"
                  style={{ opacity: visible ? 1 : 0, transition: `opacity 0.5s ease ${0.35 + i * 0.18}s` }}
                >
                  <div
                    className="w-6 h-[2px] rounded-full"
                    style={{ background: `linear-gradient(to right, ${step.color}50, ${STEPS[i + 1].color}50)` }}
                  />
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-full border"
                    style={{
                      borderColor: isLight ? 'rgba(10,22,40,0.10)' : 'rgba(255,255,255,0.10)',
                      color: isLight ? 'rgba(10,22,40,0.30)' : 'rgba(255,255,255,0.25)',
                    }}
                  >
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                  <div
                    className="w-6 h-[2px] rounded-full"
                    style={{ background: `linear-gradient(to right, ${step.color}50, ${STEPS[i + 1].color}50)` }}
                  />
                </div>
              )}
            </Fragment>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className="text-center mt-10 md:mt-14"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)', transition: 'opacity 0.6s ease 0.85s, transform 0.6s ease 0.85s' }}
        >
          <button
            onClick={() => document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 md:gap-3 bg-electric hover:bg-electric/85 text-navy-950 font-bold text-sm md:text-base px-7 md:px-10 py-3.5 md:py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap font-body shadow-lg shadow-electric/25 hover:scale-105 active:scale-95 touch-manipulation"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-circle-line text-lg"></i>
            </div>
            {t('activation_cta')}
          </button>
          <p className={`mt-2 md:mt-3 font-body text-xs ${isLight ? 'text-navy-900/35' : 'text-white/35'}`}>
            {t('activation_cta_note')}
          </p>
        </div>
      </div>
    </section>
  );
}

interface StepCardProps {
  step: typeof STEPS[number];
  index: number;
  visible: boolean;
  isLight: boolean;
  t: (key: string) => string;
}

function StepCard({ step, index, visible, isLight, t }: StepCardProps) {
  const [hovered, setHovered] = useState(false);
  const delay = index * 0.18 + 0.2;

  const cardBg = isLight ? '#ffffff' : 'rgba(12,22,48,0.85)';
  const cardBorder = hovered
    ? step.color + '70'
    : isLight ? step.color + '35' : step.color + '22';

  return (
    <div
      className="relative rounded-2xl border h-full overflow-hidden transition-all duration-300 cursor-default"
      style={{
        background: cardBg,
        borderColor: cardBorder,
        boxShadow: hovered
          ? `0 0 48px ${step.color}18, 0 20px 48px rgba(0,0,0,${isLight ? '0.08' : '0.30'})`
          : isLight ? '0 2px 12px rgba(0,0,0,0.04)' : 'none',
        opacity: visible ? 1 : 0,
        transform: visible ? (hovered ? 'translateY(-8px)' : 'translateY(0)') : 'translateY(36px)',
        transition: `opacity 0.55s ease ${delay}s, transform 0.35s cubic-bezier(0.34,1.56,0.64,1)`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{
          background: `linear-gradient(to right, ${step.color}, ${step.color}50, transparent)`,
          opacity: hovered ? 1 : 0.55,
          transition: 'opacity 0.25s ease',
        }}
      />

      {/* Side glow on hover */}
      <div
        className="absolute inset-0 pointer-events-none rounded-2xl transition-opacity duration-300"
        style={{
          background: `radial-gradient(ellipse at top left, ${step.color}08 0%, transparent 60%)`,
          opacity: hovered ? 1 : 0,
        }}
      />

      <div className="relative p-5 md:p-8">
        {/* Big decorative number */}
        <div
          className="absolute top-3 right-4 font-heading font-black select-none pointer-events-none leading-none"
          style={{
            fontSize: 'clamp(3.5rem, 14vw, 7rem)',
            color: step.color,
            opacity: hovered ? 0.10 : 0.06,
            letterSpacing: '-0.04em',
            transition: 'opacity 0.25s ease',
          }}
        >
          {step.num}
        </div>

        {/* Step badge */}
        <div
          className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full font-body mb-6 border"
          style={{ backgroundColor: step.color + '15', borderColor: step.color + '35', color: step.color }}
        >
          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: step.color }} />
          {t('activation_step_label')} {step.num}
        </div>

        {/* Icon */}
        <div
          className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-2xl mb-4 md:mb-5 border transition-all duration-300"
          style={{
            backgroundColor: hovered ? step.color + '25' : step.color + '14',
            borderColor: step.color + '35',
            color: step.color,
          }}
        >
          <i className={`${step.icon} text-xl md:text-2xl`}></i>
        </div>

        {/* Title */}
        <h3
          className="font-heading text-base md:text-xl font-bold mb-2 md:mb-3"
          style={{ color: isLight ? '#0a1628' : '#ffffff' }}
        >
          {t(step.titleKey)}
        </h3>

        {/* Description */}
        <p
          className="font-body text-xs md:text-sm leading-relaxed mb-4 md:mb-6"
          style={{ color: isLight ? 'rgba(10,22,40,0.58)' : 'rgba(255,255,255,0.55)' }}
        >
          {t(step.descKey)}
        </p>

        {/* Detail pill */}
        <div
          className="inline-flex items-center gap-2 text-xs font-semibold font-body px-3 py-1.5 rounded-full border"
          style={{ backgroundColor: step.color + '12', borderColor: step.color + '30', color: step.color }}
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`${step.detailIcon} text-xs`}></i>
          </div>
          {t(step.detailKey)}
        </div>
      </div>
    </div>
  );
}
